import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector, useDispatch } from "react-redux";
import { fetchCourses } from "../../../../../utils/Apis";
import { useTheme } from "@mui/material/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  MenuItem,
  Box,
  Typography,
  Button,
  IconButton,
  InputLabel,
  Select,
  OutlinedInput,
  Chip,
  FormControl,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const defaultStudentData = {
  isFromAdmission: true,
  createdBy: "",
  batchPeriod: {
    from: null,
    to: null,
  },
  courses: [{ course: "" }],
  feePaid: 0,
  feeTotal: 0,
  feeDue: 0,
  userDiscount: 0,
  admissionStatus: "pending",
  userStatus: "active",
};

const ProcessedAdmissionModal = ({
  open,
  onClose,
  onSubmit,
  adminId,
  selectedAdmission,
  isSubmitting,
}) => {
  const dispatch = useDispatch();
  const { courses } = useSelector((state) => state.data.courses); // fixed destructuring
  const theme = useTheme();

  const [formData, setFormData] = useState({
    ...defaultStudentData,
    createdBy: adminId,
  });

  useEffect(() => {
    if (!courses?.length) dispatch(fetchCourses());
  }, [dispatch, courses?.length]);

  // ⬇️ Populate modal data when admission selected
  useEffect(() => {
    if (selectedAdmission) {
      const selectedCourseIds = Array.isArray(selectedAdmission.courseId)
        ? selectedAdmission.courseId.map((course) => String(course._id))
        : [];

      setFormData((prev) => ({
        ...defaultStudentData,
        createdBy: adminId,
        admissionId: selectedAdmission._id || "",
        batchPeriod: selectedAdmission.batchPeriod || { from: null, to: null },
        courses: selectedCourseIds,
        feePaid: 0,
        userDiscount: 0,
        admissionStatus: "pending",
        userStatus: "active",
      }));
    }
  }, [selectedAdmission, adminId]);

  // ✅ Fee calculation based on selected courses, feePaid, and discount
  useEffect(() => {
    if (formData.courses?.length && courses?.length) {
      const selectedCourseDetails = formData.courses
        .map((id) =>
          courses.find((course) => String(course._id) === String(id))
        )
        .filter(Boolean);

      const totalFee = selectedCourseDetails.reduce(
        (sum, course) => sum + (course.feeStructure.finalFee || 0),
        0
      );

      const feeDue =
        totalFee - Number(formData.feePaid) - Number(formData.userDiscount);

      setFormData((prev) => ({
        ...prev,
        feeTotal: totalFee,
        feeDue: feeDue >= 0 ? feeDue : 0,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        feeTotal: 0,
        feeDue: 0,
      }));
    }
  }, [formData.courses, formData.feePaid, formData.userDiscount, courses]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleMultiSelect = (event) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      courses: typeof value === "string" ? value.split(",") : value.map(String),
    }));
  };

  const handleSubmit = () => {
    if (
      new Date(formData.batchPeriod.from) > new Date(formData.batchPeriod.to)
    ) {
      alert("Start date cannot be after end date.");
      return;
    }

    const finalData = {
      ...formData,
      courses: formData.courses.map((id) => ({ course: id })),
    };
    onSubmit(finalData);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: theme.shape.borderRadius, overflow: "hidden" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          Process Admission Lead - {selectedAdmission?.firstName}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Admission Status */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Admission Status</InputLabel>
              <Select
                label="Admission Status"
                name="admissionStatus"
                value={formData.admissionStatus}
                onChange={handleChange}
              >
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* User Status */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>User Status</InputLabel>
              <Select
                label="User Status"
                name="userStatus"
                value={formData.userStatus}
                onChange={handleChange}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="dropped">Dropped</MenuItem>
                <MenuItem value="terminated">Terminated</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Batch Period */}
          <Grid item xs={12}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box display="flex" gap={2}>
                <DatePicker
                  label="Start Date"
                  value={
                    formData.batchPeriod?.from
                      ? dayjs(formData.batchPeriod.from)
                      : null
                  }
                  onChange={(date) =>
                    setFormData((prev) => ({
                      ...prev,
                      batchPeriod: {
                        ...prev.batchPeriod,
                        from: date ? date.toISOString() : null,
                      },
                    }))
                  }
                  slotProps={{ textField: { fullWidth: true } }}
                />

                <DatePicker
                  label="End Date"
                  value={
                    formData.batchPeriod?.to
                      ? dayjs(formData.batchPeriod.to)
                      : null
                  }
                  onChange={(date) =>
                    setFormData((prev) => ({
                      ...prev,
                      batchPeriod: {
                        ...prev.batchPeriod,
                        to: date ? date.toISOString() : null,
                      },
                    }))
                  }
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Box>
            </LocalizationProvider>
          </Grid>

          {/* Courses */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Courses</InputLabel>
              <Select
                multiple
                name="courses"
                value={formData.courses}
                onChange={handleMultiSelect}
                input={<OutlinedInput label="Courses" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((id) => {
                      const course = courses.find((c) => c._id === id);
                      return <Chip key={id} label={course?.name || id} />;
                    })}
                  </Box>
                )}
              >
                {courses?.map((course) => (
                  <MenuItem key={course._id} value={course._id}>
                    {course.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Fee Information */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Fee Information
            </Typography>
            <Box display="flex" gap={2}>
              <TextField
                label="Fee Total"
                name="feeTotal"
                type="number"
                value={formData.feeTotal}
                InputProps={{ readOnly: true }}
                helperText="Auto-calculated from selected courses"
                fullWidth
              />
              <TextField
                label="Fee Paid"
                name="feePaid"
                type="number"
                value={formData.feePaid}
                onChange={handleChange}
                fullWidth
              />
              <TextField
                label="Discount"
                name="userDiscount"
                type="number"
                value={formData.userDiscount}
                onChange={handleChange}
                fullWidth
              />
              <TextField
                label="Fee Due"
                name="feeDue"
                type="number"
                value={formData.feeDue}
                InputProps={{ readOnly: true }}
                helperText="Auto (Total - Paid - Discount)"
                fullWidth
              />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "center", p: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={{ px: 4 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting}
          sx={{ px: 4 }}
        >
          {isSubmitting ? "Processing..." : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProcessedAdmissionModal;
